// Booking step view - qualified-creator kickoff-call booking (GRW-9).
import { Async } from '@client/new/helpers/types';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { Button } from '@design-system/components/button/button';
import { AmKey } from '../../view-model/amProfiles';
import { BookCallVM } from '../view-model/BookCall.viewModel';
import { AM_HEADSHOTS } from './amHeadshots';
import { HubspotMeetingsEmbed } from './HubspotMeetingsEmbed';
import * as styles from './BookCall.view.css';

// The top tier (`team`, GRW-330) is a round-robin pool with no single person,
// so it has no headshot — render neutral framing (title only), no <img>.
const Headshot = ({
  amKey,
  amDisplayName,
}: {
  amKey: AmKey;
  amDisplayName: string | null;
}) => {
  const src = AM_HEADSHOTS[amKey];
  if (!src) {
    return null;
  }

  return (
    <img
      className={styles.headshot}
      src={src}
      alt={amDisplayName ?? 'Your account manager'}
    />
  );
};

export const BookCallView = (vm: BookCallVM) => {
  const { state, lazyDispatch } = vm;
  const amName = state.amDisplayName ?? 'your account manager';

  if (state.booked) {
    return (
      <div className={styles.screen}>
        <div className={styles.confirmation}>
          <Headshot amKey={state.amKey} amDisplayName={state.amDisplayName} />
          <h1 className={styles.title}>You&apos;re all set!</h1>
          <p className={styles.pitch}>
            {amName} will see you on the call. We&apos;ve sent the details to
            your inbox.
          </p>
          <Button onClick={lazyDispatch('Continue')}>
            Continue to my dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.screen}>
      <div className={styles.header}>
        <Headshot amKey={state.amKey} amDisplayName={state.amDisplayName} />
        <h1 className={styles.title}>Book your kickoff call with {amName}</h1>
        <p className={styles.pitch}>
          Pick a time and {amName} will walk you through the fastest path to
          launching your strategy on Playbook.
        </p>
      </div>

      <HubspotMeetingsEmbed embedUrl={state.embedUrl} />

      <div className={styles.actions}>
        <Button variant="text" color="neutral" onClick={lazyDispatch('Skip')}>
          I&apos;ll do it later
        </Button>
      </div>
    </div>
  );
};

export const BookCallAsyncView = ({ vm }: { vm: Async<BookCallVM> }) => (
  <AsyncRenderer data={vm} renderer={(value) => <BookCallView {...value} />} />
);
